import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";

export function Sdk() {
  return (
    <>
      <Seo
        title="SDK · Vault Breaker Studios"
        description="TypeScript types, provably-fair engine, Postman collection, and Node starter for Vault Breaker integrations."
        path="/sdk"
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          SDK
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          SDK & types
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 720, lineHeight: 1.55 }}>
          Zod-typed contract definitions, a deterministic provably-fair engine, a Postman
          collection, and a 50-line Node starter. Compile against the types, vendor the engine,
          run the collection, ship the adapter.
        </p>
        <Card
          style={{
            marginTop: 16,
            padding: "12px 18px",
            background: "rgba(0,217,126,0.08)",
            border: "1px solid var(--green)",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            maxWidth: 720,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: "var(--green)",
              marginTop: 6,
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 13, color: "var(--fg-1)", lineHeight: 1.55 }}>
            <strong>Engine + types are in the public repo.</strong> The provably-fair engine is
            published as the <code className="mono">@vb/engine</code> workspace module, and the
            request/response shapes live in <code className="mono">@vb/rgs-contract</code>.
            Vendor them as a git submodule until we publish to npm.
          </span>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="Status" title="What ships today" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <Row label="@vb/rgs-contract" value="Workspace package · Zod types for every request/response" tone="ok" />
          <Row label="@vb/engine" value="Workspace package · provably-fair rollRound + verifyRound + hashServerSeed" tone="ok" />
          <Row label="Postman collection" value="examples/postman/vault-breaker.postman_collection.json — checked into the repo" tone="ok" />
          <Row label="Node starter" value="examples/operator-starter-node/ — 50-line Express adapter calling the RGS endpoints" tone="ok" />
          <Row label="@vaultbreaker/sdk-js (npm)" value="Roadmap · publishes after first paying partner integrates" tone="roadmap" />
          <Row label="Python / Go / Rust SDKs" value="Roadmap · driven by partner demand" tone="roadmap" />
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Postman collection"
          title="Click-through every endpoint"
          lead="A complete Postman collection for the public RGS surface — sandbox key issuance, session launch, round open/resolve, balance, seed reveal, and verify."
        />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Path in the repo:{" "}
            <code className="mono" style={{ color: "var(--cyan)" }}>
              examples/postman/vault-breaker.postman_collection.json
            </code>
            . Import into Postman, set the <code className="mono">api_key</code> variable to
            your sandbox key, and run the collection top-to-bottom — each request seeds
            variables for the next.
          </p>
          <pre
            style={{
              margin: 0,
              padding: "14px 18px",
              background: "var(--bg-1)",
              border: "1px solid var(--line)",
              borderRadius: 6,
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-1)",
              overflow: "auto",
              whiteSpace: "pre",
              lineHeight: 1.6,
            }}
          >{`# From the repo root
git clone https://github.com/vaultbreaker-studios/engine
open engine/examples/postman/vault-breaker.postman_collection.json

# Or run via Newman (CI-friendly)
npx newman run examples/postman/vault-breaker.postman_collection.json \\
  --env-var api_key=vb_sbx_…`}</pre>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Node starter"
          title="50-line Express adapter"
          lead="A reference operator backend that mints a session, opens a round, resolves at a target multiplier, and prints the seed reveal."
        />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Path in the repo:{" "}
            <code className="mono" style={{ color: "var(--cyan)" }}>
              examples/operator-starter-node/
            </code>
            . Set <code className="mono">VB_API_KEY</code> in <code className="mono">.env</code>,{" "}
            <code className="mono">npm i</code>, <code className="mono">npm start</code>. The
            starter exposes <code className="mono">POST /play</code> which orchestrates the full
            launch → open → resolve → reveal cycle.
          </p>
          <pre
            style={{
              margin: 0,
              padding: "14px 18px",
              background: "var(--bg-1)",
              border: "1px solid var(--line)",
              borderRadius: 6,
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-1)",
              overflow: "auto",
              whiteSpace: "pre",
              lineHeight: 1.6,
            }}
          >{`cd examples/operator-starter-node
echo "VB_API_KEY=vb_sbx_…" > .env
npm install
npm start
# → Express listening on :3000
# curl -X POST localhost:3000/play -d '{"playerId":"p1","betAmount":0.001,"target":1.5}'`}</pre>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Use the types"
          title="Compile directly against Zod"
          lead="Until the npm SDK lands, copy the contract package into your monorepo or vendor the schemas via git submodule."
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <pre
            style={{
              margin: 0,
              padding: "20px 24px",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >{`// In your operator backend (TypeScript example)
import {
  LaunchRequest,
  LaunchResponse,
  OpenRoundRequest,
  ResolveRoundRequest,
  ResolveRoundResponse,
} from "@vb/rgs-contract";

const VB_BASE = "https://vaultbreaker-api.fly.dev";
const VB_KEY  = process.env.VB_API_KEY!;

export async function launchSession(req: LaunchRequest): Promise<LaunchResponse> {
  const body = LaunchRequest.parse(req); // validates input
  const res = await fetch(\`\${VB_BASE}/rgs/session/launch\`, {
    method: "POST",
    headers: {
      "x-api-key": VB_KEY,
      "content-type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(\`launch failed \${res.status}\`);
  return LaunchResponse.parse(await res.json()); // validates output
}`}</pre>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Verify a round client-side"
          title="The engine package re-derives outcomes"
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <pre
            style={{
              margin: 0,
              padding: "20px 24px",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >{`// Run this in any context — browser, node, lambda
import { hashServerSeed, rollRound } from "@vb/engine";

const seeds = await fetch(
  \`https://vaultbreaker-api.fly.dev/rgs/round/\${roundId}/seeds\`
).then((r) => r.json());

const recomputedHash = hashServerSeed(seeds.serverSeed);
const rerolled = rollRound({
  serverSeed: seeds.serverSeed,
  clientSeed: seeds.clientSeed,
  nonce: seeds.nonce,
});

console.assert(recomputedHash === seeds.serverSeedHash, "server cheated on the commitment");
console.assert(rerolled.bustMultiplier === seeds.bustMultiplier, "outcome was tampered");`}</pre>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Adapter pattern"
          title="What a real integration looks like"
          lead="Five files, ~400 lines. We've shipped a reference one ourselves; it's the rgs-client.ts in the web app."
        />
        <Card style={{ display: "grid", gap: 10 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            The reference frontend client lives at <code>apps/web/src/lib/rgs-client.ts</code> in
            the{" "}
            {/* TODO: confirm canonical org URL before launch */}
            <a
              href="https://github.com/vaultbreaker-studios/engine"
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: "var(--cyan)", textDecoration: "none" }}
            >
              public repo
            </a>{" "}
            and is mirrored in the contract types. It demonstrates: typed fetch wrappers,
            error-shape parsing, WebSocket auth handshake, idempotent retry, and seed-reveal.
            Treat it as the canonical implementation example until the npm SDK ships.
          </p>
        </Card>
      </Section>

      <Section>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/api-reference" style={{ textDecoration: "none" }}>
            <Button>API REFERENCE →</Button>
          </Link>
          <Link to="/webhooks" style={{ textDecoration: "none" }}>
            <Button>WEBHOOKS →</Button>
          </Link>
          <Link to="/contact" style={{ textDecoration: "none" }}>
            <Button variant="gold">REQUEST EARLY SDK ACCESS</Button>
          </Link>
        </div>
      </Section>
    </>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone: "ok" | "roadmap" }) {
  const dot = tone === "ok" ? "var(--green)" : "var(--gold-bright)";
  const tag = tone === "ok" ? "Available" : "Roadmap";
  return (
    <div
      style={{
        padding: "16px 22px",
        borderTop: "1px solid var(--line)",
        display: "grid",
        gridTemplateColumns: "minmax(220px, 1fr) 2fr 100px",
        gap: 16,
        alignItems: "center",
      }}
    >
      <code className="mono" style={{ fontSize: 13, color: "var(--fg-1)" }}>
        {label}
      </code>
      <span style={{ fontSize: 13, color: "var(--fg-2)" }}>{value}</span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          fontFamily: "var(--font-ui)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          justifySelf: "end",
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: 999, background: dot }} />
        {tag}
      </span>
    </div>
  );
}
